import { LitElement, html, css } from 'lit'
import { repeat } from 'lit/directives/repeat.js'
import { StoreMixin } from '../store.js'
import { api } from '../api.js'
import { openEventStream } from '../live.js'
import { toast } from './app-toast.js'
import './triage-row.js'

// Pending Triage queue (§7.6): inbound email from unknown domains with no client
// org. Agent-only. A ticket is resolved by assigning a Workamajig company + job and
// an owner (see <triage-row>), which clears the flag and drops it out of this queue.
class PageTriage extends StoreMixin(LitElement) {
  static properties = {
    _tickets: { state: true },
    _loading: { state: true }
  }

  static styles = css`
    :host { display: block; font-family: var(--font-sans); color: var(--eggplant-500); }
    .eyebrow { display: flex; flex-direction: column; gap: 8px; align-items: flex-start; }
    .eyebrow span { font-family: var(--font-eyebrow); font-weight: 700; font-size: 12px; line-height: 1.5;
                    letter-spacing: 0.12em; text-transform: uppercase; color: var(--eggplant-500); }
    .eyebrow i { width: 20px; height: 2px; background: var(--chartreuse-500); display: block; }
    h1 { margin: 10px 0 0; font-size: 44px; font-weight: 800; letter-spacing: -0.025em; line-height: 1.05; color: var(--eggplant-500); }
    p.lede { margin: 14px 0 0; font-size: 17px; color: var(--neutral-600); max-width: 64ch; line-height: 1.5; }
    .count { display: flex; align-items: center; gap: 10px; margin: 20px 0 28px; }
    .chip { display: inline-flex; align-items: center; font-family: var(--font-eyebrow); font-weight: 500; font-size: 12px;
            letter-spacing: 0.04em; text-transform: uppercase; padding: 6px 14px; border-radius: 999px;
            background: var(--eggplant-500); color: var(--chartreuse-500); }
    .list { display: grid; gap: 16px; }
    .empty, .loading { padding: 3rem; text-align: center; color: var(--neutral-600); background: var(--white);
                       border-radius: var(--radius-card); box-shadow: var(--shadow-row); }
    @media (max-width: 720px) { h1 { font-size: 34px; } }
  `

  constructor() {
    super()
    this._tickets = []
    this._loading = true
  }

  connectedCallback() {
    super.connectedCallback()
    this._load(true)
    // Live updates (§8.5): a new inbound ticket (or one leaving triage elsewhere)
    // refreshes the queue without a manual reload.
    this._stream = openEventStream('/tickets/stream', 'tickets-changed', () => this._reloadSoon())
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this._stream?.close()
    clearTimeout(this._reloadTimer)
  }

  // Coalesce bursts (e.g. a batch of inbound mail) into a single quiet refetch.
  _reloadSoon() {
    clearTimeout(this._reloadTimer)
    this._reloadTimer = setTimeout(() => this._load(), 400)
  }

  async _load(initial = false) {
    if (initial) this._loading = true
    try {
      const data = await api.get('/tickets?view=triage')
      this._tickets = data.tickets
    } catch (err) {
      if (initial) toast(err.message || 'Failed to load the triage queue', 'error')
    } finally {
      if (initial) this._loading = false
    }
  }

  _onResolved(e) {
    const { number } = e.detail
    this._tickets = this._tickets.filter(t => t.number !== number)
  }

  render() {
    if (this._loading) return html`<div class="loading">Loading…</div>`
    const n = this._tickets.length
    return html`
      <div class="eyebrow"><span>Routing queue</span><i></i></div>
      <h1>Pending Triage</h1>
      <p class="lede">Inbound email from unrecognized domains. Assign a Workamajig company and job,
        plus an owner, to route each ticket into the queue.</p>
      ${n ? html`
        <div class="count"><span class="chip">${n} awaiting routing</span></div>
        <div class="list" @resolved=${this._onResolved}>
          ${repeat(this._tickets, t => t.number, t => html`<triage-row .ticket=${t}></triage-row>`)}
        </div>` : html`<div class="count"></div><div class="empty">Nothing awaiting triage. 🎉</div>`}
    `
  }
}

customElements.define('page-triage', PageTriage)
